import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Search, Filter, Building2, Users, Leaf, ExternalLink, ChevronRight } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import axios from 'axios';

export default function Programs() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [schemes, setSchemes] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  
  // Filters
  const [selectedPillar, setSelectedPillar] = useState(searchParams.get('pillar') || '');
  const [selectedTheme, setSelectedTheme] = useState(searchParams.get('theme') || '');
  const [selectedUseCase, setSelectedUseCase] = useState(searchParams.get('use_case') || '');
  const [selectedStatus, setSelectedStatus] = useState(searchParams.get('status') || 'active');
  
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchPillars();
    fetchSchemes();
  }, [selectedPillar, selectedTheme, selectedUseCase, selectedStatus, searchQuery]);

  const fetchPillars = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/schemes/taxonomy/pillars`);
      setPillars(data);
    } catch (error) {
      console.error('Error fetching pillars:', error);
    }
  };

  const fetchSchemes = async () => {
    // Only fetch schemes if BOTH pillar and theme are selected
    if (!selectedPillar || !selectedTheme) {
      setSchemes([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedPillar) params.append('pillar', selectedPillar);
      if (selectedTheme) params.append('theme', selectedTheme);
      if (selectedUseCase) params.append('use_case', selectedUseCase);
      if (selectedStatus) params.append('status', selectedStatus);
      if (searchQuery) params.append('search', searchQuery);
      
      const { data } = await axios.get(`${API_BASE_URL}/api/schemes/?${params.toString()}`);
      setSchemes(data);
    } catch (error) {
      console.error('Error fetching schemes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSchemes();
  };

  const clearFilters = () => {
    setSelectedPillar('');
    setSelectedTheme('');
    setSelectedUseCase('');
    setSelectedStatus('active');
    setSearchQuery('');
    setSearchParams({});
  };

  const getPillarIcon = (pillarName) => {
    if (pillarName.includes('Economic')) return '💼';
    if (pillarName.includes('Social')) return '🤝';
    if (pillarName.includes('Environmental')) return '🌱';
    return '📋';
  };

  const getPillarColor = (pillarName) => {
    if (pillarName.includes('Economic')) return 'bg-blue-100 text-blue-800 border-blue-200';
    if (pillarName.includes('Social')) return 'bg-green-100 text-green-800 border-green-200';
    if (pillarName.includes('Environmental')) return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5F8D] text-white py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl sm:text-5xl font-bold mb-4">
                Government Programs & Schemes
              </h1>
              <p className="text-lg sm:text-xl text-blue-100 mb-8">
                Discover and access government schemes aligned with CSED's three pillars of development
              </p>
              
              {/* Search Bar */}
              <form onSubmit={handleSearch} className="relative">
                <Input
                  type="text"
                  placeholder="Search schemes by name, ministry, or keywords..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 text-lg"
                />
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <Button 
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2"
                >
                  Search
                </Button>
              </form>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="flex gap-8">
            {/* Filters Sidebar */}
            <aside className={`${showFilters ? 'block' : 'hidden'} lg:block w-full lg:w-64 flex-shrink-0`}>
              <Card className="p-6 sticky top-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-lg">Filters</h3>
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    Clear All
                  </Button>
                </div>

                {/* Pillar Filter */}
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2">Pillar</label>
                  <select
                    value={selectedPillar}
                    onChange={(e) => {
                      setSelectedPillar(e.target.value);
                      setSelectedTheme(''); // Clear theme when pillar changes
                    }}
                    className="w-full border rounded-md p-2"
                  >
                    <option value="">All Pillars</option>
                    {pillars.map((pillar) => (
                      <option key={pillar.id} value={pillar.pillar_name}>
                        {getPillarIcon(pillar.pillar_name)} {pillar.pillar_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Theme Filter */}
                {selectedPillar && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-2">Theme</label>
                    <select
                      value={selectedTheme}
                      onChange={(e) => setSelectedTheme(e.target.value)}
                      className="w-full border rounded-md p-2"
                    >
                      <option value="">All Themes</option>
                      {pillars
                        .find((p) => p.pillar_name === selectedPillar)
                        ?.themes.map((theme) => (
                          <option key={theme.id} value={theme.theme_name}>
                            {theme.theme_name}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                {/* Use Case Filter */}
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2">Applicable For</label>
                  <select
                    value={selectedUseCase}
                    onChange={(e) => setSelectedUseCase(e.target.value)}
                    className="w-full border rounded-md p-2"
                  >
                    <option value="">All</option>
                    <option value="msme">MSMEs</option>
                    <option value="ngo">NGOs</option>
                    <option value="csr">CSR Programs</option>
                  </select>
                </div>

                {/* Status Filter */}
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full border rounded-md p-2"
                  >
                    <option value="active">Active</option>
                    <option value="closed">Closed</option>
                    <option value="pilot">Pilot</option>
                    <option value="">All</option>
                  </select>
                </div>
              </Card>
            </aside>

            {/* Main Content */}
            <div className="flex-1">
              {/* Mobile Filter Toggle */}
              <div className="lg:hidden mb-4">
                <Button onClick={() => setShowFilters(!showFilters)} className="w-full">
                  <Filter className="h-4 w-4 mr-2" />
                  {showFilters ? 'Hide Filters' : 'Show Filters'}
                </Button>
              </div>

              {/* Results Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">
                  {loading ? 'Loading...' : `${schemes.length} Schemes Found`}
                </h2>
              </div>

              {/* Schemes Grid */}
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E87722] mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading schemes...</p>
                </div>
              ) : (!selectedPillar || !selectedTheme) ? (
                <Card className="p-12 text-center">
                  <div className="text-blue-600 mb-4">
                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    Please Select Pillar and Theme
                  </h3>
                  <p className="text-gray-600">
                    To view government schemes, please select a <strong>Pillar</strong> from the filter, 
                    then choose a specific <strong>Theme</strong> to see relevant programs.
                  </p>
                </Card>
              ) : schemes.length === 0 ? (
                <Card className="p-12 text-center">
                  <p className="text-gray-600 text-lg">No schemes found matching your criteria.</p>
                  <Button onClick={clearFilters} className="mt-4">
                    Clear Filters
                  </Button>
                </Card>
              ) : (
                <div className="grid gap-6">
                  {schemes.map((scheme) => (
                    <Card 
                      key={scheme.id} 
                      className="p-6 hover:shadow-lg transition-shadow cursor-pointer"
                      onClick={() => navigate(`/programs/${scheme.id}`)}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={getPillarColor(scheme.primary_pillar)}>
                              {getPillarIcon(scheme.primary_pillar)} {scheme.primary_pillar}
                            </Badge>
                            {scheme.status === 'active' && (
                              <Badge className="bg-green-100 text-green-800">Active</Badge>
                            )}
                          </div>
                          <h3 className="text-xl font-bold text-[#1E3A5F] mb-2 hover:text-[#E87722]">
                            {scheme.scheme_name}
                          </h3>
                          <p className="text-sm text-gray-600 mb-2">
                            {scheme.ministry_department} • {scheme.government_level} Government
                            {scheme.launch_year && ` • Launched ${scheme.launch_year}`}
                          </p>
                        </div>
                        <ChevronRight className="h-6 w-6 text-gray-400 flex-shrink-0 ml-4" />
                      </div>

                      <p className="text-gray-700 mb-4 line-clamp-2">
                        {scheme.objective_summary}
                      </p>

                      {/* Use Case Badges */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {scheme.applies_to_msmes && (
                          <Badge variant="outline" className="text-xs">
                            <Building2 className="h-3 w-3 mr-1" />
                            MSME
                          </Badge>
                        )}
                        {scheme.applies_to_ngos && (
                          <Badge variant="outline" className="text-xs">
                            <Users className="h-3 w-3 mr-1" />
                            NGO
                          </Badge>
                        )}
                        {scheme.applies_to_csr_programs && (
                          <Badge variant="outline" className="text-xs">
                            <Leaf className="h-3 w-3 mr-1" />
                            CSR
                          </Badge>
                        )}
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between pt-4 border-t">
                        <div className="flex flex-wrap gap-2">
                          {scheme.themes.slice(0, 2).map((theme, idx) => (
                            <span key={idx} className="text-xs bg-gray-100 px-2 py-1 rounded">
                              {theme}
                            </span>
                          ))}
                          {scheme.themes.length > 2 && (
                            <span className="text-xs text-gray-500">
                              +{scheme.themes.length - 2} more
                            </span>
                          )}
                        </div>
                        {scheme.official_website && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(scheme.official_website, '_blank');
                            }}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
